import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import { api, formatApiError } from "@/lib/api";
import { useWs } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MessageSquarePlus, CheckCircle2, Trash2, Send, Lock } from "lucide-react";
import { buildExtensions } from "@/components/docs/extensions";
import { DocStatusPill, fmtWhen } from "@/components/docs/DocShared";

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `c${Date.now()}${Math.random().toString(36).slice(2)}`);

function removeCommentMark(editor, id) {
  const { state } = editor;
  const type = state.schema.marks.comment;
  const tr = state.tr;
  state.doc.descendants((node, pos) => {
    node.marks.forEach((m) => { if (m.type === type && m.attrs.commentId === id) tr.removeMark(pos, pos + node.nodeSize, m); });
  });
  tr.setMeta("allowComment", true);
  editor.view.dispatch(tr);
}

function Composer({ quote, onSave, onCancel }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="rounded-xl border border-[#C9B8FF] bg-white p-3 space-y-2 shadow-sm" data-testid="doc-comment-composer">
      <p className="text-[11px] text-[#9FA1A4] border-l-2 border-amber-300 pl-2 line-clamp-3">{quote}</p>
      <Textarea data-testid="doc-comment-input" autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add your comment…" className="bg-[#F6F6FD] border-[#EEEFEF] text-sm" />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-8 rounded-full">Cancel</Button>
        <Button size="sm" data-testid="doc-comment-save" disabled={!text.trim() || busy} onClick={async () => { setBusy(true); try { await onSave(text.trim()); } finally { setBusy(false); } }} className="h-8 rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24]">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Comment"}</Button>
      </div>
    </div>
  );
}

function CommentEditor({ doc, comments, setComments }) {
  const [sel, setSel] = useState(null);
  const editor = useEditor({
    extensions: buildExtensions({ commentOnly: true }), content: doc.content, editable: true,
    onSelectionUpdate: ({ editor: ed }) => { const { from, to } = ed.state.selection; setSel(from === to ? null : { from, to, quote: ed.state.doc.textBetween(from, to, " ").slice(0, 500) }); },
  }, [doc.id]);
  const add = async (text) => {
    const id = uid();
    editor.chain().setTextSelection({ from: sel.from, to: sel.to }).setMeta("allowComment", true).setMark("comment", { commentId: id }).run();
    try {
      const { data } = await api.post(`/docs/${doc.id}/comments`, { id, quote: sel.quote, text, content: editor.getJSON() });
      setComments((c) => [...c, data]); setSel(null); toast.success("Comment saved");
    } catch (e) { removeCommentMark(editor, id); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    removeCommentMark(editor, c.id);
    try { await api.delete(`/docs/${doc.id}/comments/${c.id}`, { data: { content: editor.getJSON() } }); setComments((cs) => cs.filter((x) => x.id !== c.id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex-1 min-h-0 flex">
      <div className="flex-1 min-w-0 overflow-y-auto"><div className="w-full px-6 md:px-12 lg:px-16 py-12">
        <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight text-[#06060C] mb-8 break-words">{doc.name}</h1>
        <EditorContent editor={editor} className="doc-prose doc-comment-mode" data-testid="client-doc-editor" />
      </div></div>
      <aside className="w-80 shrink-0 border-l border-[#EEEFEF] bg-white overflow-y-auto p-3 space-y-2" data-testid="client-doc-comments">
        {sel ? <Composer key={`${sel.from}-${sel.to}`} quote={sel.quote} onSave={add} onCancel={() => setSel(null)} />
          : <p className="text-xs text-[#9FA1A4] flex items-center gap-1.5 px-1 py-2"><MessageSquarePlus className="h-4 w-4 text-[#723FFF]" /> Highlight any text in the document to add a comment.</p>}
        {comments.map((c) => (
          <div key={c.id} data-testid="client-doc-comment" onClick={() => document.querySelector(`[data-comment-id="${c.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" })} className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2 cursor-pointer">
            <p className="text-[11px] text-[#9FA1A4] border-l-2 border-amber-300 pl-2 mb-1 line-clamp-2">{c.quote}</p>
            <p className="text-sm text-[#06060C] whitespace-pre-wrap">{c.text}</p>
            <div className="flex items-center justify-between mt-1"><span className="text-[10px] text-[#9FA1A4]">{c.author_name} · {fmtWhen(c.created_at)} · saved</span>
              {!c.submitted && <button data-testid="client-doc-comment-delete" onClick={(e) => { e.stopPropagation(); remove(c); }} className="text-[#c4c5cc] hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}</div>
          </div>
        ))}
      </aside>
    </div>
  );
}

function ViewOnly({ doc }) {
  const editor = useEditor({ extensions: buildExtensions(), content: doc.content, editable: false }, [doc.id, doc.status]);
  return (
    <div className="flex-1 min-h-0 overflow-y-auto"><div className="w-full px-6 md:px-12 lg:px-16 py-12">
      {["approved", "published"].includes(doc.status) && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 space-y-1" data-testid="client-doc-approved">
          <p className="flex items-center gap-2"><Lock className="h-4 w-4" /> Approved by {doc.approved_by} · {doc.manual_approval ? new Date(doc.approved_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", timeZone: "Australia/Brisbane" }) : fmtWhen(doc.approved_at)}</p>
          {doc.approval_note && <p className="text-emerald-900/80 pl-6">“{doc.approval_note}”</p>}
          {doc.publish_link && <p className="pl-6">Published: <a href={doc.publish_link} target="_blank" rel="noreferrer" className="underline">{doc.publish_link}</a></p>}
        </div>
      )}
      {doc.status === "feedback" && <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="client-doc-feedback-sent">Thanks! Your comments were sent to your Flight Crew.</div>}
      <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight text-[#06060C] mb-8 break-words">{doc.name}</h1>
      <EditorContent editor={editor} className="doc-prose" data-testid="client-doc-view" />
    </div></div>
  );
}

export default function ClientDocView() {
  const { docId } = useParams();
  const { slug } = useWs();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [mode, setMode] = useState("view");
  const [comments, setComments] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const load = () => api.get(`/docs/${docId}`).then(({ data }) => { if (data.source === "google") { window.location.replace(data.google_url); return; } setDoc(data); setComments(data.comments || []); if ((data.comments || []).some((c) => !c.submitted) && data.status === "with_client") setMode("comment"); }).catch(() => setDoc(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [docId]);
  const open = doc && doc.status === "with_client";
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/docs/${docId}/submit-comments`); toast.success("Comments submitted"); setMode("view"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/docs/${docId}/approve`, { note: note.trim() }); toast.success("Document approved"); setConfirm(false); setMode("view"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (doc === false) return <div className="min-h-screen flex items-center justify-center text-sm text-[#9FA1A4]">This document isn't available.</div>;
  if (!doc) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div>;
  const drafts = comments.filter((c) => !c.submitted).length;
  return (
    <div className="fixed inset-0 z-40 bg-[#FAFAFD] flex flex-col" data-testid="client-doc-page">
      <header className="shrink-0 bg-white border-b border-[#EEEFEF] px-4 md:px-6 py-3 flex items-center gap-3 flex-wrap">
        <button data-testid="client-doc-back" onClick={() => navigate(`/${slug}`)} className="text-sm text-[#723FFF] hover:text-[#5b2fd6] flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> Dashboard</button>
        <span className="text-sm font-medium text-[#06060C] truncate max-w-[40vw]" data-testid="client-doc-name">{doc.full_name}</span>
        <DocStatusPill status={doc.status} />
        {doc.sent_by && <span className="text-[11px] text-[#9FA1A4] hidden md:inline">Sent by {doc.sent_by}</span>}
        {open && (
          <div className="ml-auto flex items-center gap-2">
            {mode === "view" ? <Button data-testid="client-doc-feedback" variant="outline" onClick={() => setMode("comment")} className="rounded-full border-[#EEEFEF] h-9"><MessageSquarePlus className="h-4 w-4 mr-1.5" /> Feedback</Button>
              : <Button data-testid="client-doc-submit" disabled={!drafts || busy} onClick={submit} className="rounded-full bg-[#723FFF] text-white hover:bg-[#5b2fd6] h-9"><Send className="h-4 w-4 mr-1.5" /> Submit comments{drafts ? ` (${drafts})` : ""}</Button>}
            <Button data-testid="client-doc-approve" onClick={() => setConfirm(true)} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24] h-9"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve</Button>
          </div>
        )}
      </header>
      {open && mode === "comment" ? <CommentEditor doc={doc} comments={comments} setComments={setComments} /> : <ViewOnly doc={doc} />}
      {confirm && (
        <Dialog open onOpenChange={() => setConfirm(false)}>
          <DialogContent className="bg-white border-[#EEEFEF] max-w-md" data-testid="client-doc-approve-dialog">
            <DialogHeader><DialogTitle className="font-display">Approve this document?</DialogTitle><DialogDescription className="text-[#9FA1A4]">Your Flight Crew will be notified and the document will be locked.{drafts ? ` Your ${drafts} unsent comment${drafts === 1 ? "" : "s"} won't be submitted.` : ""}</DialogDescription></DialogHeader>
            <div><label className="text-[10px] text-[#9FA1A4] mb-1 block font-mono-data uppercase tracking-wide">Approval Note (optional)</label>
              <Textarea data-testid="client-doc-approval-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Anything your Flight Crew should know…" className="bg-[#F6F6FD] border-[#EEEFEF] text-sm" /></div>
            <DialogFooter><Button variant="outline" onClick={() => setConfirm(false)} className="rounded-full border-[#EEEFEF]">Cancel</Button><Button data-testid="client-doc-approve-confirm" disabled={busy} onClick={approve} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
